import * as amplitude from "@amplitude/analytics-browser";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Code,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Spacer,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Form, FormikProvider, useFormik } from "formik";
import { EditorState, LexicalEditor, SerializedEditorState } from "lexical";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FiAlertCircle } from "react-icons/fi";
import {
  RiDeleteBin6Line,
  RiFlaskLine,
  RiLightbulbLine,
  RiTranslate2,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { FormMustacheInput } from "src/components/Inputs/FormMustacheInput";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { PaginatedTabList } from "src/components/Layout/PaginatedTabList";
import { MaybeLink } from "src/components/Links/MaybeLink";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { Glossary } from "src/components/Text/Glossary";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import {
  LOTTERY_AND_OFFERS_DISABLED_MESSAGE,
  MESSAGE_TEMPLATE_CATEGORIES,
  MESSAGE_TEMPLATES,
  MessageTemplateCategory,
} from "src/constants";
import { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { getBestAvailableLanguage } from "src/hooks/useGetEnglishLanguageName";
import { useMessageRichContent } from "src/hooks/useMessageRichContent";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import {
  LanguageSelectorItem,
  useTranslationLanguageSelector,
} from "src/hooks/useTranslationLanguageSelector";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { GET_MESSAGE_TEMPLATE_TYPES } from "../../graphql/queries";
import { PreviewMessageTemplate } from "./components/PreviewMessageTemplate";
import { EditMessageTemplateActionBar } from "./EditMessageTemplateActionBar";
import { UPSERT_MESSAGE_TEMPLATE } from "./graphql/mutations";
import { GET_MESSAGE_TEMPLATE_AND_RICH_CONTENT } from "./graphql/queries";
import { useLexicalEditorSettings } from "./hooks/useLexicalEditorSettings";
import { useMessageTranslation } from "./hooks/useMessageTranslation";
import {
  AutomaticMessageFormSchema,
  AutomaticMessageFormType,
  MessageTemplateContent,
} from "./schemas";
import { validateMessages } from "./validators";

const FORM_ID = "edit-form-templates-edit-message-template";

/**
 * Merges incoming messages with existing messages by language: for each
 * existing message, use the incoming item when the language matches; keep
 * existing order and append any incoming languages that were not in existing.
 */
function mergeMessages(
  current: MessageTemplateContent[],
  incoming: MessageTemplateContent[]
): MessageTemplateContent[] {
  const resultByLanguage = new Map(incoming.map((m) => [m.language, m]));
  const existingLanguages = new Set(current.map((m) => m.language));
  const merged: MessageTemplateContent[] = current.map((existing) => {
    const incoming = resultByLanguage.get(existing.language);
    if (!incoming) return existing;
    return {
      ...incoming,
      content_id: existing.content_id,
    };
  });
  for (const item of incoming) {
    if (!existingLanguages.has(item.language)) {
      merged.push(item);
      existingLanguages.add(item.language);
    }
  }
  return merged;
}

interface Props {
  organization: GQL.Organization;
  formTemplateId: string;
  enrollmentPeriodId: string;
  messageTemplateType: GQL.message_template_type_enum;
  initialValues: AutomaticMessageFormType;
  existingTemplateId: string | null;
  schoolId: string | null;
  schoolName: string | null;
  schoolSpecificEnabled: boolean;
  isDisabled?: boolean;
  isViewOnly?: boolean;
  configuredLanguages: readonly string[];
  messageContext: MessageContextSchema;
}

export const EditMessageTemplateForm: FunctionComponent<Props> = ({
  organization,
  formTemplateId,
  enrollmentPeriodId,
  messageTemplateType,
  initialValues,
  existingTemplateId,
  schoolId,
  schoolName,
  schoolSpecificEnabled,
  isDisabled = false,
  isViewOnly = false,
  configuredLanguages,
  messageContext,
}) => {
  const featureFlag = useFlags(["segmented_message_warning"]);
  const [isPreview, setIsPreview] = useState(isViewOnly);

  // checks if the message template is related to lottery and offers
  const offerRelated =
    MESSAGE_TEMPLATE_CATEGORIES.find((category) =>
      category.messageTemplateTypes.includes(messageTemplateType)
    )?.key === MessageTemplateCategory.LotteryAndOffers;

  const [upsertMessageTemplate] = useRemoteDataMutation<
    GQL.UpsertMessageTemplate,
    GQL.UpsertMessageTemplateVariables
  >(UPSERT_MESSAGE_TEMPLATE);

  const toast = useAvelaToast();
  const navigate = useNavigate();

  const [emailEditor, setEmailEditor] = useState<LexicalEditor>();
  const emailEditorRef: EditorRef = useCallback((editor: LexicalEditor) => {
    if (editor != null) setEmailEditor(editor);
  }, []);

  const [smsEditor, setSmsEditor] = useState<LexicalEditor>();
  const smsEditorRef: EditorRef = useCallback((editor: LexicalEditor) => {
    if (editor != null) setSmsEditor(editor);
  }, []);

  const { getMessageDocument, uploadMessageDocument, deleteRichContent } =
    useMessageRichContent(emailEditor, smsEditor);

  const { emailEditorSettings, smsEditorSettings } =
    useLexicalEditorSettings(messageTemplateType);

  const converter = useContentConversion({
    variableMode: "expanded",
    ...emailEditorSettings,
  });

  const schoolIdParam = schoolId ? `?schoolId=${schoolId}` : "";
  const listMessageTemplatesRoute = `${FormTemplates.edit(
    organization,
    enrollmentPeriodId,
    formTemplateId
  )}${schoolIdParam}#${FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES}`;

  const saveChanges = useCallback(
    async (values: AutomaticMessageFormType) => {
      try {
        amplitude.track("Clicked save automatic message", {
          ...messageContext,
          is_active: values.enabled,
          translation_count: values.messages.length - 1,
          translated_languages: values.messages
            .map((m) => m.language)
            .filter((l) => l !== "en"),
        });

        const translationUploads = await Promise.all(
          values.messages.map(async (message) => {
            const messageDocument = await getMessageDocument({
              subject: message.subject,
              language: message.language,
              emailLexical: message.lexical
                ? converter.parse(message.lexical)
                : undefined,
              smsLexical: message.smsLexical
                ? converter.parse(message.smsLexical)
                : undefined,
            });
            if (messageDocument) {
              const { document_id } = await uploadMessageDocument(
                messageDocument
              );
              return {
                content_id: document_id,
                language: message.language,
                messageDocument,
              };
            }
            return null;
          })
        );

        const enUpload = translationUploads.find(
          (upload): upload is NonNullable<typeof upload> =>
            upload !== null && upload.language === "en"
        );

        const template = {
          ...(existingTemplateId && { id: existingTemplateId }),
          form_template_id: formTemplateId,
          type: messageTemplateType,
          school_id: schoolId,
          email_subject: enUpload?.messageDocument?.subject ?? "",
          email_plain_text: enUpload?.messageDocument?.text ?? "",
          email_markup: enUpload?.messageDocument?.html ?? "",
          sms_body: enUpload?.messageDocument?.smsText ?? "",
          enabled: values.enabled,
        };

        const rich_content_data = translationUploads
          .filter(
            (upload): upload is NonNullable<typeof upload> => upload !== null
          )
          .map(({ content_id, language }) => ({ content_id, language }));

        const insertObject = {
          ...template,
          message_template_rich_contents: {
            data: rich_content_data,
            on_conflict: {
              constraint:
                GQL.message_template_rich_content_constraint
                  .message_template_rich_content_message_id_language_key,
              update_columns: [
                GQL.message_template_rich_content_update_column.content_id,
              ],
            },
          },
        };

        const removedPersistedIds = values.removedPersistedRichContentIds ?? [];

        await upsertMessageTemplate({
          variables: {
            insert_object: insertObject,
            delete_content_ids: removedPersistedIds,
            skip_delete: removedPersistedIds.length === 0,
          },
          refetchQueries: [
            {
              query: GET_MESSAGE_TEMPLATE_AND_RICH_CONTENT,
              variables: {
                formTemplateId,
                templateType: messageTemplateType,
                schoolId: schoolId ?? "",
                skipSchool: !schoolId,
              },
            },
            {
              query: GET_MESSAGE_TEMPLATE_TYPES,
              variables: {
                formTemplateId: formTemplateId,
              },
            },
          ],
        });

        if (removedPersistedIds.length > 0) {
          await Promise.all(
            removedPersistedIds.map((contentId) => deleteRichContent(contentId))
          );
        }

        toast({
          title: "Automatic message updated",
          status: "info",
        });

        navigate(listMessageTemplatesRoute);
      } catch (err: unknown) {
        console.error(err);
        toast.genericError();
      }
    },
    [
      messageContext,
      existingTemplateId,
      formTemplateId,
      messageTemplateType,
      schoolId,
      upsertMessageTemplate,
      toast,
      navigate,
      listMessageTemplatesRoute,
      getMessageDocument,
      converter,
      uploadMessageDocument,
      deleteRichContent,
    ]
  );

  const [currentTranslationIndex, setCurrentTranslationIndex] =
    useState<number>(0);
  const {
    confirm: confirmTranslationLanguages,
    translationLanguageSelectorDialog,
  } = useTranslationLanguageSelector();

  const validate = useCallback(
    async (values: AutomaticMessageFormType, targetLanguages?: string[]) => {
      const { errors } = await validateMessages(
        values,
        emailEditor,
        smsEditor,
        featureFlag,
        targetLanguages
      );
      const zodErrors = await validateWithZod(AutomaticMessageFormSchema)(
        values
      );

      const allErrors = { ...errors, ...zodErrors };
      if (allErrors.messages && Array.isArray(allErrors.messages)) {
        setCurrentTranslationIndex(
          allErrors.messages.findIndex((m) => Object.keys(m).length > 0)
        );
      }
      return allErrors;
    },
    [emailEditor, featureFlag, smsEditor]
  );

  const formik = useFormik<AutomaticMessageFormType>({
    initialValues,
    onSubmit: saveChanges,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    validate,
  });

  // list of translation non-English languages
  const translationLanguageItems = useMemo(() => {
    const byLanguage = new Map<string, LanguageSelectorItem>();
    for (const m of formik.values.messages) {
      const lang = m.language ?? "en";
      byLanguage.set(lang, {
        language: lang,
        hasTranslation: true,
        hasFailedTranslation: !!m.error_message,
      });
    }
    for (const lang of configuredLanguages) {
      if (!byLanguage.has(lang)) {
        byLanguage.set(lang, {
          language: lang,
          hasTranslation: false,
          hasFailedTranslation: false,
        });
      }
    }
    // filter out english or no language defined
    return Array.from(byLanguage.values()).filter(
      (item): item is LanguageSelectorItem =>
        item.language !== undefined &&
        item.language !== "en" &&
        getBestAvailableLanguage(item.language) !== "en"
    );
  }, [formik.values.messages, configuredLanguages]);

  const translatableLanguages = useMemo(
    () => translationLanguageItems.map((item) => item.language),
    [translationLanguageItems]
  );

  // If there are no translatable languages or only the English language is available, disable translation
  const isTranslationDisabled =
    translationLanguageItems.length === 0 ||
    (translationLanguageItems.length === 1 &&
      translationLanguageItems[0]?.language === "en");

  const {
    isTranslating,
    handleTranslate,
    progressDrawer: translationProgressDrawer,
    previewDrawer: translationPreviewDrawer,
  } = useMessageTranslation(messageContext);

  // Initialize editors with legacy content if no lexical state exists
  useEffect(() => {
    const message = formik.values.messages[currentTranslationIndex];
    if (!message) return;

    if (!message.lexical && message.html) {
      converter.fromHtml(emailEditor, message.html);
    }
    if (!message.smsLexical && message.smsText) {
      converter.fromPlainText(smsEditor, message.smsText);
    }
  }, [
    formik.values.messages,
    currentTranslationIndex,
    converter,
    emailEditor,
    smsEditor,
  ]);

  const handleResetForm = useCallback(() => {
    setCurrentTranslationIndex(0);
    formik.resetForm({ values: initialValues });
    amplitude.track("Clicked reset automatic message", {
      ...messageContext,
    });
  }, [formik, initialValues, messageContext]);

  const onEmailFieldValueChange = useCallback(
    (
      editorState: EditorState,
      serializedValue: SerializedEditorState | string
    ) => {
      const baseName = `messages.${currentTranslationIndex}`;
      formik.setFieldValue(
        `${baseName}.html`,
        converter.toHtml(emailEditor, editorState),
        false
      );
      formik.setFieldValue(
        `${baseName}.text`,
        converter.toPlainText(emailEditor),
        false
      );
    },
    [currentTranslationIndex, converter, emailEditor, formik]
  );

  const onSmsFieldValueChange = useCallback(
    (
      editorState: EditorState,
      serializedValue: SerializedEditorState | string
    ) => {
      const baseName = `messages.${currentTranslationIndex}`;
      formik.setFieldValue(
        `${baseName}.smsText`,
        converter.toPlainText(smsEditor),
        false
      );
    },
    [currentTranslationIndex, converter, smsEditor, formik]
  );

  const onTranslateClicked = useCallback(async () => {
    if (!emailEditor || !smsEditor) {
      toast.genericError();
      console.error(
        "Unexpected error: Translation clicked but lexical one or more lexical editors not found"
      );
      return;
    }

    try {
      if (translationLanguageItems.length === 0) {
        toast.error({
          title: "No languages set to translate to",
        });
        return;
      }

      amplitude.track(
        formik.values.messages.length > 1
          ? "Clicked automatic re-translate"
          : "Clicked automatic translate",
        {
          ...messageContext,
          translatable_languages: translatableLanguages,
        }
      );

      const errors = await validate(formik.values, ["en"]);
      if (Object.keys(errors).length > 0) {
        formik.setErrors(errors);
        return;
      }

      const selectedLanguages =
        translationLanguageItems.length > 1
          ? await confirmTranslationLanguages(translationLanguageItems)
          : translatableLanguages;

      if (!selectedLanguages || selectedLanguages.length === 0) {
        amplitude.track("Clicked cancel automatic translation languages");
        return;
      }

      amplitude.track("Clicked confirm automatic translation languages", {
        selected_languages: selectedLanguages,
        translatable_languages: translatableLanguages,
      });

      const confirmedTranslations = await handleTranslate(
        formik.values,
        selectedLanguages
      );

      if (confirmedTranslations) {
        const result = confirmedTranslations.map((translation) => ({
          ...translation,
          html: converter.serialized.toHtml(emailEditor, translation.lexical),
          text:
            converter.serialized.toPlainText(
              emailEditor,
              translation.lexical
            ) ?? "",
          smsText:
            converter.serialized.toPlainText(
              smsEditor,
              translation.smsLexical
            ) ?? "",
        }));

        toast({
          title: `Translations confirmed`,
        });

        formik.setValues({
          ...formik.values,
          messages: mergeMessages(formik.values.messages, result),
        });
        setCurrentTranslationIndex(0);

        amplitude.track("Clicked confirm automatic translation", {
          ...messageContext,
          translation_count: result.length - 1,
          translated_languages: result
            .map((m) => m.language)
            .filter((l) => l !== "en"),
        });
      } else {
        amplitude.track("Clicked cancel automatic translation", {
          ...messageContext,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error({
        title: "Something went wrong.",
        description:
          "Error translating the message. Please try again or contact support.",
      });
    }
  }, [
    emailEditor,
    smsEditor,
    toast,
    translatableLanguages,
    formik,
    messageContext,
    validate,
    confirmTranslationLanguages,
    translationLanguageItems,
    handleTranslate,
    converter.serialized,
  ]);

  const handlePreviewClicked = useCallback(() => {
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        setIsPreview(true);
        amplitude.track("Clicked automatic message preview", {
          ...messageContext,
        });
      }
    });
  }, [formik, messageContext]);

  const { confirm: confirmExit, confirmationDialog: confirmationDialogExit } =
    useConfirmationDialog({
      header: "Exit without saving?",
      body: "There are unsaved changes on this form. If you exit now, you will lose your changes.",
      confirmButton: { label: "Exit without saving" },
    });

  const handleBackClicked = useCallback(async () => {
    if (isViewOnly) {
      // Preview from list; go back to list view
      navigate(listMessageTemplatesRoute);
    } else if (isPreview) {
      // Preview from editing; go back to editing
      setIsPreview(false);
      amplitude.track("Clicked automatic message preview cancel", {
        ...messageContext,
      });
    } else {
      // Editing from list; go back to list view, with warning if there are unpublished changes
      if (formik.dirty) {
        const confirmed = await confirmExit();
        if (!confirmed) {
          return;
        }
      }
      amplitude.track("Clicked automatic message edit cancel", {
        ...messageContext,
      });
      navigate(listMessageTemplatesRoute);
    }
  }, [
    confirmExit,
    formik.dirty,
    isPreview,
    isViewOnly,
    listMessageTemplatesRoute,
    messageContext,
    navigate,
  ]);

  const deleteTranslationConfirmation = useConfirmationDialog({
    header: "Remove translation?",
    body: "Are you sure you want to remove this translation?",
    confirmButton: { label: "Yes, remove" },
    cancelButton: { label: "Cancel" },
  });

  const handleDeleteTranslation = useCallback(
    async (indexToRemove: number) => {
      const messages = formik.values.messages;
      if (messages.length <= 1) return;
      const message = messages[indexToRemove];
      const languageName = message?.language_name ?? "this language";

      amplitude.track("Clicked remove automatic translation", {
        ...messageContext,
        removed_language: message?.language,
      });

      const confirmed = await deleteTranslationConfirmation.confirm({
        header: `Remove ${languageName} translation?`,
        body: (
          <VStack gap={3} align="start">
            <Text>
              Removing this translation will delete all content for{" "}
              {languageName}. Recipients who preferred {languageName} will
              receive the <b>English message</b>.
            </Text>
            <Text>This action can't be undone.</Text>
          </VStack>
        ),
        confirmButton: { label: "Yes, remove" },
        cancelButton: { label: "Cancel" },
      });

      if (!confirmed) {
        amplitude.track("Clicked cancel remove automatic translation", {
          ...messageContext,
          removed_language: message?.language,
        });
        return;
      }

      amplitude.track("Clicked confirm remove automatic translation", {
        ...messageContext,
        removed_language: message?.language,
      });
      const nextMessages = messages.filter((_, i) => i !== indexToRemove);
      const priorRemoved = formik.values.removedPersistedRichContentIds ?? [];
      const nextRemoved =
        message?.content_id && !priorRemoved.includes(message.content_id)
          ? [...priorRemoved, message.content_id]
          : priorRemoved;

      formik.setValues({
        ...formik.values,
        messages: nextMessages,
        removedPersistedRichContentIds: nextRemoved,
      });
      setCurrentTranslationIndex((prev) => {
        if (indexToRemove < prev) return prev - 1;
        if (indexToRemove === prev)
          return Math.min(prev, nextMessages.length - 1);
        return prev;
      });

      toast({
        id: "translation-removed",
        title: "Translation removed",
        description: `The ${languageName} translation has been removed.`,
      });
    },
    [formik, deleteTranslationConfirmation, messageContext, toast]
  );

  if (!formTemplateId || !messageTemplateType) {
    return <GenericError />;
  }

  return (
    <FormikProvider value={formik}>
      <Flex
        as={Form}
        id={FORM_ID}
        gap={4}
        minHeight="100%"
        direction="column"
        grow="1"
      >
        <Heading as="h2" fontSize="xl" my={4} fontWeight={600}>
          {isPreview ? "Preview " : "Edit "}
          {translationProgressDrawer}
          {translationPreviewDrawer}
          {translationLanguageSelectorDialog}
          {deleteTranslationConfirmation.confirmationDialog}
          <Glossary>
            {`${
              MESSAGE_TEMPLATES[messageTemplateType].label
            } automatic message${
              schoolSpecificEnabled && schoolId
                ? ` for ${schoolName ?? schoolId}`
                : ""
            }`}
          </Glossary>
        </Heading>
        {schoolSpecificEnabled && offerRelated && (
          <Alert status="info" borderRadius={6}>
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {schoolId
                ? `You are viewing school specific content for ${
                    schoolName ?? schoolId
                  }.`
                : `You are viewing the standard template which will be sent for all schools without school specific content enabled.`}
            </AlertDescription>
          </Alert>
        )}
        <Flex direction="column" gap={4}>
          <Heading as="h3" fontSize="lg" fontWeight={400}>
            Settings
          </Heading>

          <ConditionalTooltip
            showTooltip={!isPreview && isDisabled}
            label={
              <Text align="center">{LOTTERY_AND_OFFERS_DISABLED_MESSAGE}</Text>
            }
          >
            <Flex alignSelf="flex-start">
              <FormikSwitchInput
                label={formik.values.enabled ? "Active" : "Inactive"}
                name="enabled"
                checked={[true, ""]}
                unchecked={[false, ""]}
                direction="row"
                isDisabled={isPreview || isDisabled}
              />
            </Flex>
          </ConditionalTooltip>
          <Heading as="h3" fontSize="lg" fontWeight={400}>
            Content
          </Heading>
          <Flex gap={2} mb={2}>
            <Tag
              bg="purple.500"
              color="white"
              alignSelf="flex-start"
              size="sm"
              minWidth="60px"
            >
              <TagLeftIcon as={RiLightbulbLine} />
              <TagLabel>Tip</TagLabel>
            </Tag>
            <Text>
              Type <Code>{"{{"}</Code> in any content area to see variables.
              Variables help you replace text with dynamic content.
            </Text>
          </Flex>
          {formik.values.messages.length > 1 && (
            <Flex direction="column">
              <PaginatedTabList
                items={formik.values.messages.map((m) => ({
                  label: m.language_name,
                  value: m.language ?? m.language_name,
                  hasError: !!m.error_message,
                }))}
                currentIndex={currentTranslationIndex}
                onTabChange={setCurrentTranslationIndex}
                tabsProps={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
                renderTab={({ value, label, hasError }, index) => {
                  return (
                    <HStack spacing={1} align="center" whiteSpace="nowrap">
                      {hasError && (
                        <Tooltip label="Unable to automatically translate">
                          <Box display="inline-flex">
                            <FiAlertCircle color="red" />
                          </Box>
                        </Tooltip>
                      )}
                      <Text>{label}</Text>
                    </HStack>
                  );
                }}
              />
            </Flex>
          )}
          {isPreview ? (
            <PreviewMessageTemplate
              formValue={formik.values}
              selectedMessageIndex={currentTranslationIndex}
            />
          ) : (
            <Stack gap="5" flexGrow="1">
              {formik.values.messages[currentTranslationIndex]
                ?.error_message !== undefined && (
                <Alert
                  status="error"
                  as={Flex}
                  direction="row"
                  gap={2}
                  alignContent="flex-start"
                >
                  <AlertIcon />
                  <Flex direction="column" gap={1}>
                    <AlertTitle>Unable to automatically translate</AlertTitle>
                    <AlertDescription>
                      {
                        formik.values.messages[currentTranslationIndex]
                          ?.error_message
                      }
                    </AlertDescription>
                  </Flex>
                </Alert>
              )}

              <FormMustacheInput
                id={`messages.${currentTranslationIndex}.subject`}
                name={`messages.${currentTranslationIndex}.subject`}
                label="Subject line"
                formLabelProps={{ fontSize: "sm", color: "gray.700" }}
              />
              <FormikLexicalEditor
                name={`messages.${currentTranslationIndex}.lexical`}
                placeholder="Email content"
                height="300px"
                width="100%"
                label="Email content"
                labelProps={{ fontSize: "sm", color: "gray.700" }}
                editorRef={emailEditorRef}
                settings={emailEditorSettings}
                onFieldValueChange={onEmailFieldValueChange}
              />
              <FormikLexicalEditor
                name={`messages.${currentTranslationIndex}.smsLexical`}
                placeholder="SMS content"
                height="150px"
                width="100%"
                editorVersion="lite"
                editorRef={smsEditorRef}
                label="SMS content"
                labelProps={{ fontSize: "sm", color: "gray.700" }}
                settings={smsEditorSettings}
                onFieldValueChange={onSmsFieldValueChange}
              />
              <Flex direction="row" pt={8} gap={2}>
                <ConditionalTooltip
                  showTooltip={isTranslationDisabled}
                  label="No languages set to translate to"
                >
                  <Button
                    onClick={onTranslateClicked}
                    colorScheme="primary"
                    variant="outline"
                    leftIcon={<RiTranslate2 />}
                    isDisabled={isTranslationDisabled}
                    isLoading={isTranslating}
                    loadingText="Translating..."
                  >
                    Translate
                  </Button>
                </ConditionalTooltip>
                {formik.values.messages[currentTranslationIndex]?.language !==
                  "en" && (
                  <Tooltip
                    label={`Remove the currently selected ${formik.values.messages[currentTranslationIndex]?.language_name} translation`}
                    openDelay={400}
                  >
                    <Button
                      aria-label={`Remove the currently selected ${formik.values.messages[currentTranslationIndex]?.language_name} translation`}
                      leftIcon={<RiDeleteBin6Line />}
                      onClick={(e) => {
                        handleDeleteTranslation(currentTranslationIndex);
                      }}
                      variant="outline"
                      colorScheme="gray"
                    >
                      Remove translation
                    </Button>
                  </Tooltip>
                )}
              </Flex>
              <Flex direction="row" gap={1} align="center" pt={4}>
                <MaybeLink
                  to={Url.OrgAdmin.AvelaLabs.aboutAvelaLabs(organization)}
                  isExternal
                >
                  <Tag
                    size="sm"
                    variant="outline"
                    colorScheme="primary"
                    gap={1}
                  >
                    <Icon as={RiFlaskLine} />
                    <Text>Beta</Text>
                  </Tag>
                </MaybeLink>
              </Flex>
            </Stack>
          )}
        </Flex>
        <Spacer />
        <EditMessageTemplateActionBar
          formId={FORM_ID}
          isPreview={isPreview}
          isViewOnly={isViewOnly}
          onPreviewClicked={handlePreviewClicked}
          onBackClicked={handleBackClicked}
          hasChanges={formik.dirty}
          onClearChanges={handleResetForm}
          isSubmitting={formik.isSubmitting}
        />
        {confirmationDialogExit}
      </Flex>
    </FormikProvider>
  );
};

export const EditMessageTemplateSkeleton = () => (
  <Flex gap={4} minHeight="100%" direction="column" grow="1" p={4}>
    {/* Heading */}
    <Skeleton height="2rem" width="20rem" my={4} />

    {/* Settings section */}
    <Skeleton height="1.5rem" width="5rem" />
    <Skeleton height="1.5rem" width="8rem" />

    {/* Content section */}
    <Skeleton height="1.5rem" width="5rem" mt={4} />
    <Skeleton height="1.25rem" width="8rem" />

    {/* Email message */}
    <Stack gap={5} flexGrow={1}>
      {/* Subject line */}
      <Skeleton height="2.5rem" />

      {/* Email editor */}
      <Grid
        templateColumns="repeat(auto-fit, minmax(30rem, 1fr))"
        gap={5}
        templateRows="auto"
      >
        <GridItem>
          <Skeleton height="300px" />
        </GridItem>
      </Grid>

      {/* SMS section */}
      <Skeleton height="1.25rem" width="8rem" />
      <Skeleton height="150px" />
    </Stack>
  </Flex>
);
